import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { uploadTemplateImage, MAX_COVER_BLOCKS } from "../../../services/templatesApi";

// Right-hand configuration panel for the template editor. Both tabs follow one
// order — Layout (structure) -> Content (the words) -> Style (colour) ->
// Assets (images) — because the structural choices decide what the rest of the
// panel should even show. Driven by a normalized config object;
// `onChange(path, value)` sets a value at a dotted path, e.g.
// onChange("cover_page.colors.headings", "#910000").

// --- primitives -------------------------------------------------------------

const Group = ({ title, children }) => (
  <div className="pt-4">
    <div className="text-[9.5px] font-semibold text-[#A1A1AA] uppercase tracking-[0.12em] pb-2 border-b border-[#E4E4E7] mb-3">
      {title}
    </div>
    {children}
  </div>
);

const Segmented = ({ value, options, onChange }) => (
  <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
    {options.map((o) => {
      const active = value === o.value;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-[8px] border text-[12px] font-medium transition-colors ${
            active
              ? "border-[#BB42AE] bg-[#BB42AE]/[0.08] text-[#BB42AE]"
              : "border-[#E4E4E7] text-[#52525B] hover:bg-[#FAFAFA]"
          }`}
        >
          {o.icon}
          <span>{o.label}</span>
        </button>
      );
    })}
  </div>
);

const RadioRow = ({ value, options, onChange }) => (
  <div className="flex flex-col gap-2.5">
    {options.map((o) => {
      const active = value === o.value;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className="flex items-center gap-2.5 text-left"
        >
          <span
            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
              active ? "border-[#BB42AE]" : "border-[#C4C4C8]"
            }`}
          >
            {active && <span className="w-2 h-2 rounded-full bg-[#BB42AE]" />}
          </span>
          <span className={`text-[14px] ${active ? "text-[#18181B]" : "text-[#52525B]"}`}>
            {o.label}
          </span>
        </button>
      );
    })}
  </div>
);

const INPUT =
  "w-full px-2.5 py-2 rounded-[6px] border border-[#E4E4E7] text-[13px] text-[#18181B] " +
  "placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#BB42AE] focus:ring-1 focus:ring-[#BB42AE]";

const TextField = ({ value, onChange, placeholder, rows }) =>
  rows ? (
    <textarea
      rows={rows}
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`${INPUT} resize-y leading-snug`}
    />
  ) : (
    <input
      type="text"
      value={value || ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={INPUT}
    />
  );

// Label first, then the value — matching every other row in the panel. The hex
// field commits on blur/Enter: firing per keystroke wrote half-typed values like
// "#ab", which the server silently reverted to the previous colour.
const ColorField = ({ label, value, onChange }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    const v = (draft || "").trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(v)) onChange(v);
    else setDraft(value); // invalid -> snap back rather than silently dropping it
  };
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[13px] font-medium text-[#3F3F46]">{label}</span>
      <div className="flex items-center gap-2 shrink-0">
        <input
          type="text"
          value={draft || ""}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
            if (e.key === "Escape") { setDraft(value); e.currentTarget.blur(); }
          }}
          spellCheck={false}
          className="w-[82px] px-2 py-1.5 rounded-[6px] border border-[#E4E4E7] text-[13px] text-[#18181B] focus:outline-none focus:border-[#BB42AE]"
        />
        <label className="relative w-7 h-7 rounded-[6px] border border-[#E4E4E7] overflow-hidden cursor-pointer shrink-0">
          <span className="block w-full h-full" style={{ backgroundColor: value }} />
          <input
            type="color"
            value={/^#[0-9A-Fa-f]{6}$/.test(value || "") ? value : "#000000"}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
          />
        </label>
      </div>
    </div>
  );
};

const Toggle = ({ enabled, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!enabled)}
    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
      enabled ? "bg-[#2DAD4E]" : "bg-[#E4E4E7]"
    }`}
  >
    <div
      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
        enabled ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

const SwitchRow = ({ label, hint, enabled, onChange }) => (
  <div className="flex items-start justify-between gap-3 py-1">
    <div>
      <div className="text-[13px] font-medium text-[#3F3F46]">{label}</div>
      {hint && <p className="text-[11px] text-[#A1A1AA] mt-0.5 leading-snug">{hint}</p>}
    </div>
    <Toggle enabled={enabled} onChange={onChange} />
  </div>
);

const ImageUpload = ({ value, onChange, label }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await uploadTemplateImage(file);
      onChange(url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={busy}
        className="w-full flex items-center justify-between px-3 py-2 rounded-[8px] border border-dashed border-[#C4C4C8] text-[14px] text-[#52525B] hover:bg-[#FAFAFA] transition-colors"
      >
        <span className="truncate">{busy ? "Uploading…" : value ? "Replace image" : label}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      </button>
      {value && (
        <div className="flex items-center justify-between mt-2">
          <img src={value} alt="" className="h-10 rounded-[6px] border border-[#E4E4E7] object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-[12px] text-[#BB42AE] hover:underline"
          >
            Remove
          </button>
        </div>
      )}
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={pick} />
    </div>
  );
};

// --- cover line editor ------------------------------------------------------

// The cover's free-form lines, edited here rather than by hovering the preview.
// Reordering is a real drag, armed only from the grip so dragging inside an
// input still selects text.
const LineEditor = ({ blocks, onChange, selected, onSelect }) => {
  const [armed, setArmed] = useState(false);
  const [from, setFrom] = useState(null);
  const [over, setOver] = useState(null);

  const write = (next) => onChange("cover_page.blocks", next);
  const setAt = (i, key, v) => write(blocks.map((b, j) => (j === i ? { ...b, [key]: v } : b)));
  const removeAt = (i) => {
    write(blocks.filter((_, j) => j !== i));
    onSelect?.(null);
  };
  const add = () => {
    write([...blocks, { label: "", value: "" }]);
    onSelect?.(blocks.length);
  };
  const drop = (to) => {
    if (from == null || to === from) return;
    const next = blocks.slice();
    next.splice(to, 0, next.splice(from, 1)[0]);
    write(next);
    onSelect?.(to);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[12px] font-medium text-[#3F3F46]">Cover lines</span>
        <span className="text-[10px] text-[#A1A1AA] tabular-nums">
          {blocks.length} / {MAX_COVER_BLOCKS}
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        {blocks.map((b, i) => (
          <div
            key={i}
            draggable={armed}
            onDragStart={() => setFrom(i)}
            onDragOver={(e) => { e.preventDefault(); setOver(i); }}
            onDrop={() => { drop(i); setFrom(null); setOver(null); setArmed(false); }}
            onDragEnd={() => { setFrom(null); setOver(null); setArmed(false); }}
            onFocus={() => onSelect?.(i)}
            className={`grid items-center gap-1.5 rounded-[6px] ${
              over === i && from !== null && from !== i ? "ring-1 ring-[#BB42AE]" : ""
            } ${from === i ? "opacity-50" : ""}`}
            style={{ gridTemplateColumns: "18px minmax(0,1fr) minmax(0,1.2fr) 24px" }}
          >
            <span
              title="Drag to reorder"
              onMouseDown={() => setArmed(true)}
              onMouseUp={() => setArmed(false)}
              className="h-6 flex items-center justify-center text-[#C4C4C8] hover:text-[#52525B] cursor-grab select-none text-[12px] leading-none"
            >
              ⠿
            </span>
            <input
              type="text"
              value={b.label || ""}
              placeholder="No label"
              onChange={(e) => setAt(i, "label", e.target.value)}
              className={`min-w-0 px-2 py-1.5 rounded-[6px] border text-[12px] text-[#18181B] placeholder:text-[#C4C4C8] focus:outline-none focus:border-[#BB42AE] ${
                selected === i ? "border-[#BB42AE]" : "border-[#E4E4E7]"
              }`}
            />
            <input
              type="text"
              value={b.value || ""}
              placeholder="Text"
              onChange={(e) => setAt(i, "value", e.target.value)}
              className={`min-w-0 px-2 py-1.5 rounded-[6px] border text-[12px] text-[#18181B] placeholder:text-[#C4C4C8] focus:outline-none focus:border-[#BB42AE] ${
                selected === i ? "border-[#BB42AE]" : "border-[#E4E4E7]"
              }`}
            />
            <button
              type="button"
              title="Remove line"
              onClick={() => removeAt(i)}
              className="w-6 h-6 flex items-center justify-center rounded-[5px] text-[#A1A1AA] hover:bg-[#FEF2F2] hover:text-[#DC2626] text-[13px] leading-none"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {blocks.length === 0 && (
        <p className="text-[11px] text-[#A1A1AA] py-1">
          No lines yet — add the details that belong on the cover.
        </p>
      )}

      {blocks.length < MAX_COVER_BLOCKS && (
        <button
          type="button"
          onClick={add}
          className="w-full mt-2 py-2 rounded-[6px] border border-dashed border-[#BB42AE] bg-[#BB42AE]/[0.06] text-[12px] font-medium text-[#BB42AE] hover:bg-[#BB42AE]/[0.12]"
        >
          + Add line
        </button>
      )}
    </div>
  );
};

// --- control sets -----------------------------------------------------------

const CoverControls = ({ config, onChange, selectedBlock, onSelectBlock }) => {
  const cover = config.cover_page;
  return (
    <>
      <Group title="Layout">
        <SwitchRow
          label="Artwork only"
          hint="The image is the whole cover. Hides all text and the logo."
          enabled={!!cover.artwork_only}
          onChange={(v) => onChange("cover_page.artwork_only", v)}
        />
        {!cover.artwork_only && (
          <div className="pt-3">
            <div className="text-[11px] text-[#A1A1AA] mb-1.5">Alignment</div>
            <Segmented
              value={cover.content_layout}
              onChange={(v) => onChange("cover_page.content_layout", v)}
              options={[
                { value: "left", label: "Left" },
                { value: "centered", label: "Center" },
                { value: "right", label: "Right" },
              ]}
            />
          </div>
        )}
      </Group>

      {!cover.artwork_only && (
        <>
          <Group title="Content">
            <div className="flex flex-col gap-2">
              <TextField
                value={cover.fields.title}
                placeholder="Title — e.g. Project Proposal"
                onChange={(v) => onChange("cover_page.fields.title", v)}
              />
              <TextField
                rows={2}
                value={cover.fields.subtitle}
                placeholder="Subtitle — optional"
                onChange={(v) => onChange("cover_page.fields.subtitle", v)}
              />
            </div>
            <div className="pt-4">
              <LineEditor
                blocks={cover.blocks || []}
                onChange={onChange}
                selected={selectedBlock}
                onSelect={onSelectBlock}
              />
            </div>
          </Group>

          <Group title="Style">
            <div className="flex flex-col gap-3">
              <ColorField label="Headings" value={cover.colors.headings} onChange={(v) => onChange("cover_page.colors.headings", v)} />
              <ColorField label="Body text" value={cover.colors.body} onChange={(v) => onChange("cover_page.colors.body", v)} />
            </div>
          </Group>
        </>
      )}

      <Group title="Assets">
        <ImageUpload
          label="Upload background"
          value={cover.background_image_url}
          onChange={(v) => onChange("cover_page.background_image_url", v)}
        />
        {!cover.artwork_only && (
          <div className="pt-3">
            <ImageUpload label="Upload logo" value={config.logo.url} onChange={(v) => onChange("logo.url", v)} />
            <p className="text-[11px] text-[#A1A1AA] mt-2">
              Drag the logo on the preview to position it, and use the corner handle to resize.
            </p>
          </div>
        )}
      </Group>
    </>
  );
};

const BodyControls = ({ config, onChange }) => {
  const body = config.body_pages;
  const hasHeader = body.header_style !== "none";
  return (
    <>
      <Group title="Layout">
        <div className="text-[11px] text-[#A1A1AA] mb-1.5">Header style</div>
        <RadioRow
          value={body.header_style}
          onChange={(v) => onChange("body_pages.header_style", v)}
          options={[
            { value: "accent_line", label: "Accent Line" },
            { value: "colour_band", label: "Colour Band" },
            { value: "none", label: "None" },
          ]}
        />
        <div className="pt-4">
          <div className="text-[11px] text-[#A1A1AA] mb-1.5">Page margins</div>
          <Segmented
            value={body.page_margins}
            onChange={(v) => onChange("body_pages.page_margins", v)}
            options={[
              { value: "small", label: "Small" },
              { value: "large", label: "Large" },
            ]}
          />
        </div>
      </Group>

      <Group title="Content">
        <div className="flex flex-col gap-3">
          <div>
            <div className="text-[11px] text-[#A1A1AA] mb-1.5">Header text</div>
            <TextField
              rows={2}
              value={body.header_text}
              placeholder={hasHeader ? "Shown opposite the logo. Enter adds a line." : "Needs a header style"}
              onChange={(v) => onChange("body_pages.header_text", v)}
            />
            {!hasHeader && (
              <p className="text-[11px] text-[#A1A1AA] mt-1">
                Header style is None, so this won't appear.
              </p>
            )}
          </div>
          <div>
            <div className="text-[11px] text-[#A1A1AA] mb-1.5">Footer text</div>
            <TextField
              rows={2}
              value={body.footer_text}
              placeholder="Defaults to the proposal name"
              onChange={(v) => onChange("body_pages.footer_text", v)}
            />
          </div>
          <SwitchRow
            label="Show footer"
            hint="Footer text and page numbers on every body page."
            enabled={body.page_number.show}
            onChange={(v) => onChange("body_pages.page_number.show", v)}
          />
        </div>
      </Group>

      <Group title="Style">
        <div className="flex flex-col gap-3">
          <ColorField label="Headings" value={body.colors.headings} onChange={(v) => onChange("body_pages.colors.headings", v)} />
          <ColorField label="Body text" value={body.colors.body} onChange={(v) => onChange("body_pages.colors.body", v)} />
        </div>
      </Group>
    </>
  );
};

const ConfigControls = ({ tab, config, onChange, selectedBlock, onSelectBlock }) =>
  tab === "body" ? (
    <BodyControls config={config} onChange={onChange} />
  ) : (
    <CoverControls
      config={config}
      onChange={onChange}
      selectedBlock={selectedBlock}
      onSelectBlock={onSelectBlock}
    />
  );

export default ConfigControls;
